import type { BlockType } from '@emailforge/schemas';

import { Icon, type IconName } from '@/components/ui/icons';
import { addBlock } from '@/features/editor/editorSlice';
import { BLOCK_LIBRARY, createBlock } from '@/screens/app/editor/blockDefaults';
import { useAppDispatch } from '@/store/hooks';

const iconFor: Record<BlockType, IconName> = {
  hero: 'hero',
  heading: 'heading',
  text: 'text',
  button: 'button',
  image: 'image',
  columns: 'columns',
  divider: 'divider',
  spacer: 'spacer',
  product: 'product',
  footer: 'footer',
  social: 'social',
};

export function BlockLibrary() {
  const dispatch = useAppDispatch();
  const groups = groupBy(BLOCK_LIBRARY, (b) => b.category);

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 18 }}>
      {Object.entries(groups).map(([group, items]) => (
        <div key={group}>
          <div
            className="mono"
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              color: 'var(--ink-3)',
              marginBottom: 8,
            }}
          >
            {group}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
            {items.map((b) => {
              const IconCmp = Icon[iconFor[b.type]];
              return (
                <button
                  key={b.type}
                  className="mf-card"
                  style={{
                    padding: 10,
                    cursor: 'grab',
                    border: '1px solid var(--line)',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    background: 'var(--paper)',
                  }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/x-block-type', b.type);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={() => dispatch(addBlock({ block: createBlock(b.type) }))}
                  title={`Click to append · drag to position`}
                >
                  <span style={{ color: 'var(--ink-2)', display: 'flex' }}>
                    <IconCmp size={14} />
                  </span>
                  <div style={{ fontSize: 12, fontWeight: 550 }}>{b.label}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink-3)', lineHeight: 1.4 }}>{b.description}</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function groupBy<T, K extends string>(items: T[], key: (x: T) => K): Record<K, T[]> {
  return items.reduce(
    (acc, item) => {
      const k = key(item);
      (acc[k] ??= []).push(item);
      return acc;
    },
    {} as Record<K, T[]>,
  );
}
